import axios from 'axios';
import { publicIssueMockApi } from '../mocks/publicIssueMock';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
const IS_MOCK_MODE = process.env.REACT_APP_MOCK_MODE === 'true';

const api = axios.create({
  baseURL: `${API_BASE_URL}/v1/public`,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const unwrap = (response) => response?.data?.data;
const isNetworkError = (error) => !error?.response;

const isMockEnabled = () => {
  if (IS_MOCK_MODE) {
    return true;
  }
  const params = new URLSearchParams(window.location.search);
  if (params.get('mock') === '0') {
    localStorage.removeItem('PUBLIC_ISSUE_MOCK_MODE');
    return false;
  }
  if (params.get('mock') === '1') {
    localStorage.setItem('PUBLIC_ISSUE_MOCK_MODE', 'true');
    return true;
  }
  return localStorage.getItem('PUBLIC_ISSUE_MOCK_MODE') === 'true';
};

const withFallback = async (realCall, mockCall) => {
  if (isMockEnabled()) {
    return mockCall();
  }
  try {
    return await realCall();
  } catch (error) {
    if (isNetworkError(error)) {
      return mockCall();
    }
    throw error;
  }
};

export const publicIssueApi = {
  startSession: async (issueToken) => {
    return withFallback(
      async () => {
        const response = await api.post('/issue-sessions/start', { issue_token: issueToken });
        return unwrap(response);
      },
      async () => publicIssueMockApi.startSession(issueToken),
    );
  },

  getSession: async (sessionId) => {
    return withFallback(
      async () => {
        const response = await api.get(`/issue-sessions/${sessionId}`);
        return unwrap(response);
      },
      async () => publicIssueMockApi.getSession(sessionId),
    );
  },

  startPass: async (sessionId) => {
    return withFallback(
      async () => {
        const response = await api.post('/pass/start', { session_id: sessionId });
        return unwrap(response);
      },
      async () => publicIssueMockApi.startPass(sessionId),
    );
  },

  completePass: async ({ sessionId, name, phone, passResult = 'success' }) => {
    return withFallback(
      async () => {
        const response = await api.post('/pass/callback', {
          session_id: sessionId,
          pass_result: passResult,
          name,
          phone,
        });
        return unwrap(response);
      },
      async () => publicIssueMockApi.completePass({ sessionId, name, phone, passResult }),
    );
  },

  requestAccountVerification: async ({ sessionId, bankCode, accountNumber, accountHolderName }) => {
    return withFallback(
      async () => {
        const response = await api.post('/account-verifications/request', {
          session_id: sessionId,
          bank_cd: bankCode,
          account_number: accountNumber,
          account_holder_name: accountHolderName,
        });
        return unwrap(response);
      },
      async () => publicIssueMockApi.requestAccountVerification({
        sessionId,
        bankCode,
        accountNumber,
        accountHolderName,
      }),
    );
  },

  confirmAccountVerification: async ({ sessionId, verificationCode }) => {
    return withFallback(
      async () => {
        const response = await api.post('/account-verifications/confirm', {
          session_id: sessionId,
          verification_code: verificationCode,
        });
        return unwrap(response);
      },
      async () => publicIssueMockApi.confirmAccountVerification({ sessionId, verificationCode }),
    );
  },

  issueVirtualAccount: async ({ sessionId, depositorName, amount = 0, bankCode }) => {
    return withFallback(
      async () => {
        const response = await api.post('/virtual-accounts', {
          session_id: sessionId,
          depositorName,
          amount,
          bank_cd: bankCode,
        });
        return unwrap(response);
      },
      async () => publicIssueMockApi.issueVirtualAccount({ sessionId, depositorName, amount, bankCode }),
    );
  },
};
